import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/ClientLayout';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Users, MoreHorizontal, Edit, Trash2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import axios from 'axios';

const CrewsPage = () => {
  const { token, API } = useAuth();
  const [crews, setCrews] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCrew, setEditingCrew] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    leader_id: '',
    member_ids: [],
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [crewsRes, usersRes] = await Promise.all([
        axios.get(`${API}/crews`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { users: [] } }))
      ]);
      setCrews(crewsRes.data.crews);
      setUsers(usersRes.data.users || []);
    } catch (error) {
      toast.error('Failed to fetch crews');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Crew name is required');
      return;
    }

    try {
      if (editingCrew) {
        await axios.put(`${API}/crews/${editingCrew.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Crew updated successfully');
      } else {
        await axios.post(`${API}/crews`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Crew created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save crew');
    }
  };

  const openEditDialog = (crew) => {
    setEditingCrew(crew);
    setFormData({
      name: crew.name,
      leader_id: crew.leader_id || '',
      member_ids: crew.member_ids || [],
      notes: crew.notes || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCrew(null);
    setFormData({ name: '', leader_id: '', member_ids: [], notes: '' });
  };

  const getUserName = (id) => users.find(u => u.id === id)?.name || 'Unknown';

  const filteredCrews = crews.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <ClientLayout title="Crews">
      <Card className="border-gray-200">
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search crews..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-crews-input"
              />
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90" data-testid="add-crew-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Crew
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingCrew ? 'Edit Crew' : 'Create New Crew'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Crew Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Team Alpha"
                      data-testid="crew-name-input"
                    />
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Input
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Specializes in commercial properties..."
                    />
                  </div>
                  <Button className="w-full bg-primary hover:bg-primary/90" onClick={handleSubmit} data-testid="save-crew-btn">
                    {editingCrew ? 'Save Changes' : 'Create Crew'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredCrews.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No crews found</p>
              <Button variant="outline" onClick={() => setDialogOpen(true)}>
                Create Your First Crew
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCrews.map((crew) => (
                <div 
                  key={crew.id}
                  className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                  data-testid={`crew-${crew.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{crew.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          crew.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {crew.status}
                        </span>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(crew)}>
                          <Edit className="h-4 w-4 mr-2" /> Edit
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  {crew.notes && (
                    <p className="text-sm text-gray-500 mb-2">{crew.notes}</p>
                  )}
                  
                  <div className="text-sm text-gray-500">
                    Members: {crew.member_ids?.length || 0}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </ClientLayout>
  );
};

export default CrewsPage;
